.contact-section {
  text-align: left;
  margin: 30px 0;

  &:first-child {
    margin-top: 0;
  }

  h3 {
    color: #2c3e50;
    font-size: 14px;
    margin: 35px 0 20px;
    text-align: left;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;

    &:first-child {
      margin-top: 0;
    }
  }

  .contact-info {
    .info-item {
      display: flex;
      align-items: center;
      gap: 8px;
      margin: 10px 0;

      i {
        color: #666;
      }

      span {
        color: #4a5568;
        font-size: 16px;
      }
    }
  }
}<template>
  <web-app-layout :showLogo="true" :noPadding="true">
    <div v-if="!isAuthenticated" class="auth-redirect">
      <SmartBanner />
    </div>

    <div class="profile-container">
      <div v-if="state.error" class="error-message">
        {{ state.error }}
      </div>

      <profile-page-skeleton v-if="loading" />

      <template v-else-if="state.profile">
        <!-- Background with Avatar and Name on top -->
        <div
          class="profile-header"
          :style="{ backgroundImage: `url(${state.profile?.background || defaultBackground})` }"
        >
          <!-- Dark overlay for better text visibility -->
          <div class="profile-header-overlay" />

          <div class="profile-content-overlay">
            <div class="profile-avatar">
              <img
                :src="state.profile?.avatar || defaultAvatar"
                alt="Profile Avatar"
                @error="handleAvatarError"
              />
            </div>
            <div class="profile-name-section">
              <h1 class="user-name">{{ fullName }}</h1>
            </div>
          </div>
        </div>

        <!-- Profile Content Section -->
        <div class="profile-content-container">
          <!-- Tabs Section - Only show if there are stickers -->
          <div v-if="hasStickers" class="tabs-container">
            <div class="tab-buttons">
              <div class="tab-navigation">
                <button
                  type="button"
                  :class="['tab-button', { active: state.activeTab === 'about' }]"
                  @click="state.activeTab = 'about'"
                >
                  ABOUT
                </button>
                <button
                  type="button"
                  :class="['tab-button', { active: state.activeTab === 'shop' }]"
                  @click="state.activeTab = 'shop'"
                >
                  SHOP
                </button>
              </div>
              <div v-if="state.activeTab === 'shop'" class="search-container">
                <el-input
                  v-model="searchString"
                  placeholder="Search"
                  class="sticker-search"
                  @input="handleStickerSearch"
                  clearable
                >
                  <template #prefix>
                    <el-icon><Search /></el-icon>
                  </template>
                </el-input>
              </div>
            </div>

            <!-- About Tab Content -->
            <div v-if="state.activeTab === 'about'" class="tab-content">
              <div class="profile-info-section">
                <!-- My Why Section -->
                <div class="bio-section" v-if="state.profile?.why">
                  <h2 class="purpose-title">{{ state.profile.why }}</h2>
                </div>

                <div class="bio-section" v-if="state.profile?.bio">
                  <h3>BIO</h3>
                  <p>{{ state.profile.bio }}</p>
                </div>

                <!-- Values Section -->
                <div class="values-section" v-if="state.profile?.values?.length">
                  <h3>VALUES</h3>
                  <div class="tags-container">
                    <el-tag
                      v-for="value in state.profile.values"
                      :key="value.id"
                      class="value-tag"
                      :class="{ favorite: value.favorite }"
                    >
                      {{ value.value }}
                    </el-tag>
                  </div>
                </div>

                <!-- Interests Section -->
                <div class="interests-section" v-if="state.profile?.interests?.length">
                  <h3>INTERESTS</h3>
                  <div class="tags-container">
                    <el-tag
                      v-for="interest in state.profile.interests"
                      :key="interest.id"
                      class="interest-tag"
                      :class="{ favorite: interest.favorite }"
                    >
                      {{ interest.interest }}
                    </el-tag>
                  </div>
                </div>

                <!-- Bullhorns Section -->
                <div class="values-section" v-if="state.profile?.bullhorns?.length">
                  <h3>CAUSES</h3>
                  <div class="tags-container">
                    <el-tag
                      v-for="bullhorn in state.profile.bullhorns"
                      :key="bullhorn.id"
                      class="interest-tag"
                    >
                      {{ bullhorn.bullhorn }}
                    </el-tag>
                  </div>
                </div>

                <!-- Channels Section -->
                <div class="values-section" v-if="state.profile?.channels?.length">
                  <h3>CHANNELS</h3>
                  <div class="tags-container">
                    <el-tag
                      v-for="channel in state.profile.channels"
                      :key="channel.id"
                      class="channel-tag"
                    >
                      {{ channel.channel }}
                    </el-tag>
                  </div>
                </div>
              </div>
            </div>

            <!-- Shop Tab Content -->
            <div v-if="state.activeTab === 'shop'" class="tab-content">
              <div class="profile-info-section">
                <div class="stickers-section">
                  <div class="stickers-container">
                    <div v-if="stickerSearchLoading" class="sticker-search-loading">
                      <el-icon :size="30" class="loading-spinner">
                        <Loading />
                      </el-icon>
                      <p>Searching stickers...</p>
                    </div>
                    <div
                      v-else-if="filteredStickers.length === 0 && searchString"
                      class="no-search-results"
                    >
                      <p>No stickers found matching "{{ searchString }}"</p>
                    </div>
                    <div
                      v-else-if="filteredStickers.length === 0"
                      class="no-search-results"
                    >
                      <p>No stickers yet</p>
                    </div>
                    <StickersGridTemplate
                      v-else
                      :stickers="filteredStickers"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- No Tabs - Show About content directly when no stickers -->
          <div v-else class="profile-info-section">
            <!-- My Why Section -->
            <div class="bio-section" v-if="state.profile?.why">
              <h2 class="purpose-title">{{ state.profile.why }}</h2>
            </div>

            <div class="bio-section" v-if="state.profile?.bio">
              <h3>BIO</h3>
              <p>{{ state.profile.bio }}</p>
            </div>

            <!-- Values Section -->
            <div class="values-section" v-if="state.profile?.values?.length">
              <h3>VALUES</h3>
              <div class="tags-container">
                <el-tag
                  v-for="value in state.profile.values"
                  :key="value.id"
                  class="value-tag"
                  :class="{ favorite: value.favorite }"
                >
                  {{ value.value }}
                </el-tag>
              </div>
            </div>

            <!-- Interests Section -->
            <div class="interests-section" v-if="state.profile?.interests?.length">
              <h3>INTERESTS</h3>
              <div class="tags-container">
                <el-tag
                  v-for="interest in state.profile.interests"
                  :key="interest.id"
                  class="interest-tag"
                  :class="{ favorite: interest.favorite }"
                >
                  {{ interest.interest }}
                </el-tag>
              </div>
            </div>

            <!-- Bullhorns Section -->
            <div class="values-section" v-if="state.profile?.bullhorns?.length">
              <h3>CAUSES</h3>
              <div class="tags-container">
                <el-tag
                  v-for="bullhorn in state.profile.bullhorns"
                  :key="bullhorn.id"
                  class="interest-tag"
                >
                  {{ bullhorn.bullhorn }}
                </el-tag>
              </div>
            </div>

            <!-- Channels Section -->
            <div class="values-section" v-if="state.profile?.channels?.length">
              <h3>CHANNELS</h3>
              <div class="tags-container">
                <el-tag
                  v-for="channel in state.profile.channels"
                  :key="channel.id"
                  class="channel-tag"
                >
                  {{ channel.channel }}
                </el-tag>
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>
  </web-app-layout>
</template>

<script lang="ts">
import {
  defineComponent,
  onMounted,
  reactive,
  computed,
  watch,
  ref,
} from 'vue';
import { Loading, Search } from '@element-plus/icons-vue';
import { useStore } from 'vuex';
import { useRouter, useRoute } from 'vue-router';
import WebAppLayout from '@/views/web-app/dashboard/WebAppLayout.vue';
import ProfilePageSkeleton from '@/views/web-app/components/ProfilePageSkeleton.vue';
import gqlRequest from '@/views/web-app/dashboard/gql-request';
import defaultAvatarImg from '@/assets/images/default-profile.jpeg';
import defaultBackgroundImg from '@/assets/images/bg.jpg';
import SmartBanner from '@/views/web-app/sticker/SmartBanner.vue';
import StickersGridTemplate from '@/views/web-app/sticker/StickersGridTemplate.vue';

interface ProfileData {
  profile_url?: string;
  id?: string;
  avatar?: string;
  background?: string;
  profile_qr_code?: string;
  bio?: string;
  why?: string;
  user?: {
    id?: string;
    firstname?: string;
    lastname?: string;
    title?: string;
    email?: string;
    phone?: string;
  };
  address_line_1?: string;
  address_line_2?: string;
  city?: string;
  state?: string;
  zipcode?: string;
  values?: Array<{
    id: string;
    value: string;
    favorite: boolean;
  }>;
  interests?: Array<{
    id: string;
    interest: string;
    favorite: boolean;
  }>;
  bullhorns?: Array<{
    id: string;
    bullhorn: string;
  }>;
  channels?: Array<{
    id: string;
    channel: string;
  }>;
}

export default defineComponent({
  name: 'UserProfile',
  components: {
    WebAppLayout,
    ProfilePageSkeleton,
    SmartBanner,
    StickersGridTemplate,
    Loading,
    Search,
  },
  setup() {
    const {
      loading,
      fetchProfile,
      fetchPublicProfile,
      fetchPublicProfileBySlug,
      getSocialShopStickers,
    } = gqlRequest();
    const store = useStore();
    const router = useRouter();
    const route = useRoute();

    const state = reactive({
      profile: null as ProfileData | null,
      error: null as string | null,
      userStickers: [] as any[],
      activeTab: 'about' as string,
    });

    const searchString = ref('');
    const debouncedSearchString = ref('');
    const searchDebounceTimer = ref<number | null>(null);
    const stickerSearchLoading = ref(false);

    const defaultAvatar = defaultAvatarImg;
    const defaultBackground = defaultBackgroundImg;

    const currentUserId = computed(() => store.getters['users/me']?.id);
    const profileUserId = computed(() => route.params.id as string);
    const profileUserSlug = computed(() => route.params.slug as string);
    const isSlugRoute = computed(() => !!profileUserSlug.value);

    const isOwnProfile = computed(() => {
      if (!state.profile?.user?.id) return false;
      return currentUserId.value === state.profile.user.id;
    });

    const isAuthenticated = computed(() => store.getters['users/isMeSignedIn']);

    const fullName = computed(() => {
      if (!state.profile?.user) return 'Loading...';
      return `${state.profile.user.firstname || ''} ${state.profile.user.lastname || ''}`.trim() || 'No name set';
    });

    const filteredStickers = computed(() => {
      const query = debouncedSearchString.value.trim().toLowerCase();
      if (!query) {
        return state.userStickers;
      }
      return state.userStickers.filter((sticker) => {
        const title = (sticker?.title || '').toLowerCase();
        const description = (sticker?.description || '').toLowerCase();
        return title.includes(query) || description.includes(query);
      });
    });

    async function checkUserStickers(userId: string) {
      try {
        const stickers = await getSocialShopStickers({
          socialShopUserId: userId,
        });
        state.userStickers = stickers || [];
      } catch (error) {
        console.error('Error fetching user stickers:', error);
        state.userStickers = [];
      }
    }

    function handleStickerSearch(query: string) {
      searchString.value = query;

      if (searchDebounceTimer.value) {
        clearTimeout(searchDebounceTimer.value);
      }

      if (!query.trim()) {
        stickerSearchLoading.value = false;
        debouncedSearchString.value = '';
        return;
      }

      stickerSearchLoading.value = true;

      searchDebounceTimer.value = setTimeout(() => {
        debouncedSearchString.value = query.trim();
        stickerSearchLoading.value = false;
      }, 500) as any;
    }

    const hasStickers = computed(() => state.userStickers.length > 0);

    async function loadProfile() {
      const identifier = profileUserId.value || profileUserSlug.value;

      // If no identifier is provided and user is authenticated, redirect to own profile
      if (!identifier && currentUserId.value) {
        router.push(`/web/profile/user/${currentUserId.value}`);
        return;
      }

      // If no identifier and not authenticated, show error
      if (!identifier) {
        state.error = 'Profile not found';
        return;
      }

      try {
        loading.value = true;
        state.error = null;
        searchString.value = '';
        debouncedSearchString.value = '';
        let profile: ProfileData | null = null;

        // Handle slug-based routes
        if (isSlugRoute.value) {
          profile = await fetchPublicProfileBySlug(profileUserSlug.value);
        } else if (isAuthenticated.value && currentUserId.value === profileUserId.value) {
          // If authenticated and viewing own profile, use fetchProfile for full data
          profile = await fetchProfile();
        } else {
          // For other users' profiles or unauthenticated users, use public profile
          profile = await fetchPublicProfile(profileUserId.value);
        }

        if (profile) {
          state.profile = profile;

          // Check user stickers after profile is loaded
          if (profile.user?.id) {
            await checkUserStickers(profile.user.id);

            // Check if URL has ?tab=shop and user has stickers, then set shop as active tab
            if (route.query.tab === 'shop' && state.userStickers.length > 0) {
              state.activeTab = 'shop';
            }
          }
        } else {
          state.error = 'Profile not found';
        }
      } catch (error) {
        console.error('Error loading profile:', error);
        state.error = 'Failed to load profile data';
        if (error.message?.includes('not authorized')) {
          const currentPath = route.fullPath;
          router.push(`/auth/web/signin?redirect=${encodeURIComponent(currentPath)}`);
        }
      } finally {
        loading.value = false;
      }
    }

    onMounted(() => {
      loadProfile();
    });

    watch(() => route.params.id, (newId) => {
      if (newId) {
        loadProfile();
      }
    });

    watch(() => route.params.slug, (newSlug) => {
      if (newSlug) {
        loadProfile();
      }
    });

    const handleAvatarError = (event: Event) => {
      const target = event.target as HTMLImageElement;
      if (!target) return;
      
      // Prevent infinite loop if default image also fails
      if (target.src === defaultAvatar) return;
      
      target.src = defaultAvatar;
    };

    return {
      state,
      loading,
      defaultAvatar,
      defaultBackground,
      fullName,
      isAuthenticated,
      isOwnProfile,
      hasStickers,
      filteredStickers,
      searchString,
      stickerSearchLoading,
      handleStickerSearch,
      handleAvatarError,
    };
  },
});
</script>

<style lang="scss" scoped>
.profile-container {
  max-width: 1200px;
  margin: 0;
  padding: 0;
  padding-top: 24px;
  padding-bottom: 64px;
  text-align: left;
  
  @media (min-width: 768px) {
    padding-top: 32px;
    padding-bottom: 80px;
  }
  
  @media (min-width: 1024px) {
    padding-top: 40px;
    padding-bottom: 96px;
  }
}

.profile-content-container {
  margin-top: 20px;
  text-align: left;
  padding: 0 24px;
  
  @media (min-width: 768px) {
    padding: 0 40px;
  }
  
  @media (min-width: 1024px) {
    padding: 0 64px;
  }
}

.profile-header {
  height: 300px;
  background-size: cover;
  background-position: center;
  position: relative;
  border-radius: 20px;
  margin: 24px 24px 30px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  padding: 40px;

  @media (min-width: 768px) {
    margin: 32px 40px 30px;
  }

  @media (min-width: 1024px) {
    margin: 40px 64px 30px;
  }

  /* Mobile responsive */
  @media (max-width: 768px) {
    height: 280px;
    padding: 15px;
  }
}

.profile-header-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  border-radius: 20px;
  z-index: 1;
}

.profile-content-overlay {
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  gap: 30px;
  width: 100%;
  padding-top: 100px;

  /* Mobile responsive - column layout */
  @media (max-width: 768px) {
    flex-direction: column;
    justify-content: center;
    text-align: center;
    padding-top: 0;
    gap: 20px;
  }
}

.profile-avatar {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  border: 4px solid white;
  overflow: hidden;
  background: #fff;
  box-shadow: none;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  /* Mobile responsive */
  @media (max-width: 768px) {
    width: 100px;
    height: 100px;
    order: 1;
  }
}

.profile-name-section {
  color: white;
  text-align: left;

  /* Mobile responsive - center text */
  @media (max-width: 768px) {
    text-align: center;
    order: 2;
  }

  .user-name {
    color: white;
    font-size: 28px;
    font-weight: 700;
    margin: 0 0 10px 0;
    letter-spacing: -0.5px;

    /* Mobile responsive */
    @media (max-width: 768px) {
      font-size: 24px;
    }
  }

  .user-title {
    color: rgba(255, 255, 255, 0.9);
    font-size: 16px;
    margin: 0;
    font-weight: 500;

    /* Mobile responsive */
    @media (max-width: 768px) {
      font-size: 14px;
    }
  }
}

.profile-info-section {
  padding: 0;
  background: transparent;
  border-radius: 0;
  box-shadow: none;

  h3 {
    color: #1a1a1a;
    font-size: 13px;
    margin: 40px 0 16px;
    text-align: left;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 1px;

    &:first-child {
      margin-top: 0;
    }
  }
}

.bio-section {
  text-align: left;
  margin: 32px 0;
  padding: 0;
  background: transparent;
  border-radius: 0;

  &:first-child {
    margin-top: 0;
  }

  p {
    color: #374151;
    line-height: 1.7;
    margin: 0;
    font-size: 16px;
    
    @media (min-width: 768px) {
      font-size: 17px;
      line-height: 1.8;
    }
  }
}

.values-section, .interests-section {
  text-align: left;
  margin: 32px 0;

  &:first-child {
    margin-top: 0;
  }
}

.tags-container {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin: 16px 0 0 0;
  padding: 0;
}

.value-tag, .interest-tag, .channel-tag {
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 600;
  border-radius: 9999px;
  transition: all 0.2s ease;
  background-color: #f0fdfa !important;
  color: #0f766e !important;
  border: 1px solid #99f6e4 !important;

  &.favorite {
    background: linear-gradient(135deg, #0d9488 0%, #14b8a6 100%) !important;
    color: white !important;
    border: 1px solid transparent !important;
    box-shadow: 0 4px 12px rgba(13, 148, 136, 0.3);
  }

  &:hover {
    transform: translateY(-2px);
    background-color: #ccfbf1 !important;
    border-color: #5eead4 !important;
    box-shadow: 0 4px 12px rgba(13, 148, 136, 0.15);
  }
}

/* Purpose title styling */
.purpose-title {
  color: #1a1a1a;
  font-size: 26px;
  margin: 0 0 24px 0;
  text-align: left;
  font-weight: 800;
  line-height: 1.4;
  
  @media (min-width: 768px) {
    font-size: 28px;
  }
}

.contact-section {
  text-align: left;
  margin: 32px 0;

  &:first-child {
    margin-top: 0;
  }

  h3 {
    color: #1a1a1a;
    font-size: 13px;
    margin: 40px 0 16px;
    text-align: left;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 1px;

    &:first-child {
      margin-top: 0;
    }
  }

  .contact-info {
    .info-item {
      display: flex;
      align-items: center;
      gap: 10px;
      margin: 12px 0;

      i {
        color: #6b7280;
      }

      span {
        color: #374151;
        font-size: 15px;
      }
    }
  }
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  padding: 80px 24px;
  text-align: center;

  @media (min-width: 768px) {
    padding: 100px 40px;
  }

  @media (min-width: 1024px) {
    padding: 120px 64px;
  }
}

.loading-spinner {
  width: 48px;
  height: 48px;
  border: 4px solid #e5e7eb;
  border-top-color: #0d9488;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 16px auto;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.loading-text {
  font-size: 16px;
  color: #6b7280;
  margin: 0;
}

.auth-redirect {
  text-align: center;
  padding: 80px 24px;
  font-size: 18px;
  color: #6b7280;
  background: #f9fafb;
  border-radius: 16px;
  margin: 40px 24px;

  @media (min-width: 768px) {
    padding: 100px 40px;
    margin: 40px 40px;
  }

  @media (min-width: 1024px) {
    padding: 120px 64px;
    margin: 40px 64px;
  }
}

.error-message {
  background-color: #fef2f2;
  color: #dc2626;
  padding: 20px 24px;
  border-radius: 12px;
  margin: 24px 24px;
  text-align: center;
  font-size: 15px;
  font-weight: 600;
  border: 1px solid #fecaca;
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.1);

  @media (min-width: 768px) {
    padding: 24px 40px;
    margin: 32px 40px;
  }

  @media (min-width: 1024px) {
    padding: 24px 64px;
    margin: 40px 64px;
  }
}

/* Tab System Styles */
.tabs-container {
  margin-bottom: 32px;
  text-align: left;
  padding: 0;
}

.tab-buttons {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
  position: relative;
  border-bottom: 2px solid #e5e7eb;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
}

.tab-navigation {
  display: flex;
  align-items: center;
  gap: 8px;
}

.search-container {
  display: flex;
  align-items: center;
  padding-bottom: 8px;

  .sticker-search {
    width: 280px;

    :deep(.el-input__wrapper) {
      box-shadow: 0 0 0 1px #dcdfe6 inset;
      border-radius: 8px;
      transition: all 0.3s ease;

      &:hover {
        box-shadow: 0 0 0 1px #005C6B inset;
      }

      &.is-focus {
        box-shadow: 0 0 0 1px #1fbeaf inset;
      }
    }

    :deep(.el-input__inner) {
      font-size: 14px;

      &::placeholder {
        color: #a8a8a8;
      }
    }

    :deep(.el-input__prefix-inner) {
      color: #a8a8a8;
    }

    @media (max-width: 768px) {
      width: 100%;
      max-width: 350px;
    }
  }
}

.tab-button {
  background: none;
  border: none;
  padding: 14px 20px;
  font-size: 13px;
  font-weight: 800;
  color: #9ca3af;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  &::after {
    content: '';
    position: absolute;
    bottom: -2px;
    left: 0;
    right: 0;
    height: 3px;
    background-color: transparent;
    transition: background-color 0.2s ease;
  }

  &:hover {
    color: #0d9488;
  }

  &.active {
    color: #1a1a1a;

    &::after {
      background: linear-gradient(90deg, #0d9488 0%, #14b8a6 100%);
    }
  }
  
  @media (max-width: 640px) {
    padding: 12px 16px;
    font-size: 12px;
  }
}

.tab-content {
  animation: fadeIn 0.3s ease-in;
  padding: 0;

  .profile-info-section {
    padding: 0;
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.stickers-section {
  text-align: left;
  margin-top: 0;

  h3 {
    margin-top: 0;
    margin-bottom: 24px;
  }
}

.stickers-container {
  margin-top: 0;
}

.sticker-search-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  gap: 15px;

  p {
    color: #5c6c7c;
    font-size: 16px;
    margin: 0;
  }

  .loading-spinner {
    color: #1fbeaf;
    animation: rotate 2s linear infinite;
  }
}

.no-search-results {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  padding: 40px 20px;

  p {
    color: #6b7280;
    font-size: 16px;
    margin: 0;
    text-align: center;
    font-style: italic;
  }
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
